import {Component, ElementRef, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {ICsPagingParams} from "@app/shared/cs-components/cs-components.interface";
import {TCsPagingEvent} from "@app/shared/cs-components/cs-components.types";

@Component({
    selector: 'app-cs-pagination-panel',
    templateUrl: './cs-pagination-panel.component.html',
    styleUrls: ['cs-pagination-panel.component.scss']
})
export class CsPaginationPanelComponent implements OnInit {

    /** Sentinel value in pageList for window mode: render as "..." */
    readonly PAGE_ELLIPSIS = 0;

    /** Max page numbers to create (avoids crash when total record count is huge, e.g. 250000) */
    private static readonly MAX_PAGES = 500;

    @Input() pageOffsetList = [5, 10, 20, 50, 100];
    @Input() pageList: number[];
    @Input() initialParams: ICsPagingParams;
    @Input() maxPagesDisplayLength = 10;
    @Input() dropUpCalcAddOnSpace = 0;  // Should be in `px` unit.

    @Input() set totalRecord(value: number) {
        this._totalRecord = value;
        if (this.pagingParams && value >= 0) {
            const pageSize = this.pagingParams.pageSize || 5;
            this.setPageSize(pageSize, 'none');
        }
    }

    @Output() onUpdatePagingParams = new EventEmitter<ICsPagingParams>();
    @Output() onInit = new EventEmitter<{ reset: () => void }>();

    pagingParams: ICsPagingParams;
    isDropUp = false;
    _totalRecord: number;
    /** Total number of pages (can be very large); pageList may be capped for display */
    totalPagesCount: number;
    /** True when total pages exceed MAX_PAGES and we show a sliding window (no ellipsis logic) */
    get isWindowMode(): boolean {
        return this.totalPagesCount > CsPaginationPanelComponent.MAX_PAGES;
    }

    constructor(private _hostRef: ElementRef) {
    }

    ngOnInit() {
        this.setInitialPagingParams();
        this.setPageList();
        this.setPaginationProps();
        this.updatePagingParams();
        this.init();
    }

    init() {
        this.onInit.emit({reset: this.resetPagingList});
    }

    onMenuShown() {
        this.setDropUp();
    }

    setDropUp() {
        const ddMenuHeight = (29 * this.pageOffsetList.length) + 2;
        const bottomBoundary = document.getElementsByTagName('body')[0].getBoundingClientRect().bottom - (this.dropUpCalcAddOnSpace || 0);
        const dropDownBottomPosition = this._hostRef.nativeElement.getBoundingClientRect().bottom;
        const availableBottomSpace = bottomBoundary - dropDownBottomPosition;

        this.isDropUp = availableBottomSpace <= ddMenuHeight;
    }

    setInitialPagingParams() {
        this.pagingParams = {
            currentPage: (this.initialParams && this.initialParams.currentPage) || 1,
            pageSize: (this.initialParams && this.initialParams.pageSize) || 5,
            startRow: null,
            endRow: null
        };
    }

    setPageList() {
        const pageSize = this.pagingParams.pageSize || 1;
        const total = this._totalRecord != null && this._totalRecord >= 0 ? this._totalRecord : 0;
        this.totalPagesCount = Math.ceil(total / pageSize) || 0;
        if (this.totalPagesCount <= CsPaginationPanelComponent.MAX_PAGES) {
            this.pageList = this.totalPagesCount ? Array(this.totalPagesCount).fill(0).map((_, i) => i + 1) : [];
        } else {
            const current = this.pagingParams.currentPage || 1;
            const half = Math.floor(this.maxPagesDisplayLength / 2);
            const start = Math.max(1, Math.min(current - half, this.totalPagesCount - this.maxPagesDisplayLength + 1));
            const end = Math.min(this.totalPagesCount, start + this.maxPagesDisplayLength - 1);
            const size = end - start + 1;
            this.pageList = Array(size).fill(0).map((_, i) => start + i);
            if (this.totalPagesCount > end) {
                if (this.totalPagesCount > end + 1) {
                    this.pageList.push(this.PAGE_ELLIPSIS);
                }
                this.pageList.push(this.totalPagesCount);
            }
        }
    }

    setPaginationProps() {
        const total = this._totalRecord != null && this._totalRecord >= 0 ? this._totalRecord : 0;
        const remainingLastPageRowCount = total % this.pagingParams.pageSize;
        this.pagingParams.startRow = (this.pagingParams.pageSize * (this.pagingParams.currentPage - 1)) + 1;
        this.pagingParams.endRow = this.totalPagesCount === this.pagingParams.currentPage && remainingLastPageRowCount ? (this.pagingParams.startRow - 1) + remainingLastPageRowCount : Math.min(this.pagingParams.pageSize * this.pagingParams.currentPage, total);
    }

    setPageSize(offset?: number, pagingEvent?: TCsPagingEvent, currentPage?: number) {
        this.pagingParams.currentEvent = pagingEvent || 'pageSize';
        this.pagingParams.pageSize = offset;
        this.pagingParams.currentPage = currentPage || Math.trunc(((this.pagingParams.startRow - 1) / this.pagingParams.pageSize)) + 1;
        this.setPageList();
        this.setPaginationProps();
        this.updatePagingParams();
    }

    setCurrentPage(page: number) {
        this.pagingParams.currentEvent = 'pageNav';
        this.pagingParams.currentPage = page;
        this.setPageList();
        this.setPaginationProps();
        this.updatePagingParams();
    }

    resetPagingList = () => this.setPageSize(this.pagingParams.pageSize, 'none', 1);

    updatePagingParams() {
        this.onUpdatePagingParams.emit(this.pagingParams);
    }
}
